import { useTranslation } from "react-i18next";
import ImageSelector from "../../dashboard/settings/branding/ImageSelector";
import InputColor from "../../dashboard/settings/branding/InputColor";
import { GradientRange } from "../../../parts/inputs/GradientRange";
import StepHeading from "./StepHeading";

const Toggle = ({ label, name, value, onChange }) => (
  <div className="switch-container">
    <input name={name} value={value} hidden readOnly />
    <span>{label}</span>
    <button
      type="button"
      aria-pressed={value}
      aria-label={label}
      onClick={() => onChange({ target: { name, value: !value } })}
    >
      <img
        className="toggle-button"
        src={`/img/new-icons/toggle${value ? "_on" : "_off"}.svg`}
        alt=""
        style={{ position: "relative" }}
      />
    </button>
  </div>
);

// Step 5 — logo and splash. Reuses the dashboard ImageSelector, which carries
// file state through hidden inputs (imageInputs) for multipart submission.
const StepLogoSplash = ({ current, state, mobile, onChange }) => {
  const { t } = useTranslation("auth");
  const {
    logoImg,
    formLogoImg = {},
    mobileSplashImg,
    formMobileSplashImg = {},
    mobileSplashBg,
    formMobileSplashBg = {},
    whiteBg,
    isSplashImgTinted,
    splashOverlay,
    splashOverlayColor,
    splashOverlayOpacity,
    logoChanged,
    mobileSplashChanged,
    mobileSplashBgChanged,
  } = state;

  return (
    <div className="step-body step-logo-splash">
      <StepHeading
        current={current}
        title={t("onboardingWizard.logoAndSplash.title")}
        subtitle={t("onboardingWizard.logoAndSplash.subtitle")}
      />

      <div className="applogo-placeholder">
        <ImageSelector
          mobile={mobile}
          title={t("onboardingBranding.inAppLogo")}
          selectorTitle={t("onboardingBranding.uploadLogo")}
          subInfo={t("onboardingBranding.imageMaxSize")}
          toolTipTextBody={t("onboardingBranding.inAppLogoTooltip")}
          forOnboarding
          additionalContent={
            <Toggle
              label={t("onboardingBranding.whiteLogoBackground")}
              name="whiteBg"
              value={whiteBg}
              onChange={onChange}
            />
          }
          additionalStyle={{
            marginTop: 16,
            marginBottom: 16,
            backgroundColor: whiteBg ? "#fff" : null,
          }}
          value={logoImg}
          onChange={onChange}
          inputName="customBranding[logoImg]"
          id="logoImg"
          imageInputs={[
            { name: "logoChanged", value: logoChanged },
            { name: "formLogoImgFile", value: formLogoImg.file || "" },
            { name: "formLogoImgType", value: formLogoImg.type || "" },
            { name: "formLogoImgName", value: formLogoImg.name || "" },
            { name: "formLogoImgS3Path", value: formLogoImg.s3Path },
          ]}
          showButton={false}
          blackBg
        />

        <ImageSelector
          mobile={mobile}
          title={t("onboardingBranding.splashScreenLogo")}
          selectorTitle={t("onboardingBranding.uploadLogo")}
          subInfo={t("onboardingBranding.imageMaxSize")}
          toolTipTextBody={t("onboardingBranding.splashLogoTooltip")}
          forOnboarding
          value={mobileSplashImg}
          onChange={onChange}
          additionalStyle={{ marginTop: 16, marginBottom: 16 }}
          additionalContent={
            <Toggle
              label={t("onboardingBranding.tintSplashscreenLogo")}
              name="tintSplashImg"
              value={isSplashImgTinted}
              onChange={onChange}
            />
          }
          inputName="customBranding[mobileSplashImg]"
          id="mobileSplashImg"
          imageInputs={[
            { name: "mobileSplashChanged", value: mobileSplashChanged },
            {
              name: "formMobileSplashImgFile",
              value: formMobileSplashImg.file || "",
            },
            {
              name: "formMobileSplashImgType",
              value: formMobileSplashImg.type || "",
            },
            {
              name: "formMobileSplashImgName",
              value: formMobileSplashImg.name || "",
            },
            {
              name: "formMobileSplashImgS3Path",
              value: formMobileSplashImg.s3Path,
            },
          ]}
          showButton={false}
          blackBg
        />

        <ImageSelector
          mobile={mobile}
          title={t("onboardingBranding.backgroundImage")}
          selectorTitle={t("onboardingBranding.uploadImage")}
          subInfo={t("onboardingBranding.imageMaxSize")}
          toolTipTextBody={t("onboardingBranding.backgroundImageTooltip")}
          forOnboarding
          value={mobileSplashBg}
          onChange={onChange}
          additionalContent={
            <div className="overlay-controls">
              <Toggle
                label={t("onboardingBranding.overlay")}
                name="splashOverlay"
                value={splashOverlay}
                onChange={onChange}
              />
              {splashOverlay ? (
                <>
                  <InputColor
                    mobile={mobile}
                    inputName="splashOverlayColor"
                    inputNameColor="splashOverlayColorPicker"
                    value={splashOverlayColor}
                    onChange={onChange}
                  />
                  <div>
                    <div className="flex justify-between items-center my-2">
                      <span>{t("onboardingBranding.opacity")}</span>
                      <span>{splashOverlayOpacity}</span>
                    </div>
                    <GradientRange
                      onChange={({ target: { value } }) =>
                        onChange({
                          target: {
                            name: "splashOverlayOpacity",
                            value: parseInt(value, 10),
                          },
                        })
                      }
                      rangeName="splashOverlayOpacity"
                      rangeValue={splashOverlayOpacity}
                    />
                  </div>
                </>
              ) : null}
            </div>
          }
          additionalStyle={{ marginTop: 16, marginBottom: 16 }}
          inputName="customBranding[mobileSplashBg]"
          id="mobileSplashBg"
          imageInputs={[
            { name: "mobileSplashBgChanged", value: mobileSplashBgChanged },
            {
              name: "formMobileSplashBgFile",
              value: formMobileSplashBg.file || "",
            },
            {
              name: "formMobileSplashBgType",
              value: formMobileSplashBg.type || "",
            },
            {
              name: "formMobileSplashBgName",
              value: formMobileSplashBg.name || "",
            },
            {
              name: "formMobileSplashBgS3Path",
              value: formMobileSplashBg.s3Path,
            },
          ]}
          showButton={false}
          blackBg
        />
      </div>
    </div>
  );
};

export default StepLogoSplash;
